import { Component } from '@theme/component';

/**
 * `<feature-grid-tabs>` — master-detail category tabs for the feature-grid
 * section. All tab panels are server-rendered (first one visible); this
 * component only moves `aria-selected`/`hidden` and implements the WAI-ARIA
 * tabs keyboard pattern with a roving tabindex.
 *
 * @extends {Component}
 */
class FeatureGridTabs extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#tablist?.addEventListener('keydown', this.#handleKeydown);

    // Keep the customizer usable: selecting a block activates its tab.
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#handleBlockSelect);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#tablist?.removeEventListener('keydown', this.#handleKeydown);

    if (window.Shopify?.designMode) {
      document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    }
  }

  get #tablist() {
    return this.querySelector('[role="tablist"]');
  }

  get #tabs() {
    return Array.from(this.querySelectorAll('[role="tab"]'));
  }

  /**
   * Activates the clicked tab.
   *
   * @param {MouseEvent} event
   */
  selectTab(event) {
    const tab = event.target;
    if (!(tab instanceof HTMLElement)) return;

    this.#activate(tab);
  }

  /**
   * Moves selection to `tab`: roving tabindex on the tabs, `hidden` on the
   * panels, and a retriggered enter animation on the visible panel.
   *
   * @param {HTMLElement} tab
   * @param {{ focus?: boolean }} [options]
   */
  #activate(tab, { focus = false } = {}) {
    for (const other of this.#tabs) {
      const selected = other === tab;
      other.setAttribute('aria-selected', selected ? 'true' : 'false');
      other.tabIndex = selected ? 0 : -1;
    }

    for (const panel of this.querySelectorAll('[role="tabpanel"]')) {
      panel.hidden = true;
    }

    const panelId = tab.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : null;
    if (panel) {
      panel.hidden = false;
      panel.classList.remove('is-entering');
      // Force a reflow so the enter animation replays on every switch.
      void panel.offsetWidth;
      panel.classList.add('is-entering');
    }

    if (focus) tab.focus();
  }

  #handleKeydown = (event) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    const tabs = this.#tabs;
    if (tabs.length === 0) return;

    const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const currentIndex = active ? tabs.indexOf(active) : -1;
    const fromIndex = currentIndex === -1
      ? tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
      : currentIndex;

    let nextIndex;
    if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = tabs.length - 1;
    } else {
      const delta = event.key === 'ArrowRight' ? 1 : -1;
      nextIndex = (fromIndex + delta + tabs.length) % tabs.length;
    }

    event.preventDefault();
    this.#activate(tabs[nextIndex], { focus: true });
  };

  #handleBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;

    let tab = target.closest('[role="tab"]');
    if (!tab) {
      const panel = target.closest('[role="tabpanel"]');
      if (panel?.id) tab = this.querySelector(`[aria-controls="${panel.id}"]`);
    }

    if (tab instanceof HTMLElement) this.#activate(tab);
  };
}

if (!customElements.get('feature-grid-tabs')) {
  customElements.define('feature-grid-tabs', FeatureGridTabs);
}
